// src/api/axiosInstance.ts
import axios, { AxiosError, AxiosResponse } from 'axios';
import { getStoredToken, getStoredRefreshToken, storeTokens, clearTokens, tokensRotatedSince } from '../auth/auth';
import { isDevAuthBypassEnabled } from '../auth/devAuthBypass';
import { ApiError, AuthError, NotFoundError } from '../errors/ApiError';

/** flock-be TenantMiddleware resolves tenant from this header (must match webapp.tenants.subdomain). */
function setXTenantSubdomainHeader(headers: unknown, tenantSub: string): void {
  if (!tenantSub || headers == null) return;
  const h = headers as { set?: (k: string, v: string) => void; [key: string]: unknown };
  if (typeof h.set === 'function') {
    h.set('X-Tenant-Subdomain', tenantSub);
    return;
  }
  h['X-Tenant-Subdomain'] = tenantSub;
}

const resolvedTenantSubdomain =
  process.env.REACT_APP_TENANT_SUBDOMAIN?.trim() ||
  (process.env.NODE_ENV === 'development' ? 'demo' : '');

// Create Axios instance
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || '/',
  timeout: 60000,
});

if (resolvedTenantSubdomain) {
  setXTenantSubdomainHeader(axiosInstance.defaults.headers.common, resolvedTenantSubdomain);
}

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

let onSessionExpired: (() => void) | null = null;

// Function to set the session expired callback
export const setSessionExpiredCallback = (callback: () => void) => {
  onSessionExpired = callback;
};

// Request interceptor to attach the token
axiosInstance.interceptors.request.use(
  (config) => {
    if (resolvedTenantSubdomain) {
      setXTenantSubdomainHeader(config.headers, resolvedTenantSubdomain);
    }
    if (!config.skipAuth) { // Custom flag to skip auth
      const token = getStoredToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor to handle errors and token refresh.
// `skipAuth` requests (login, refresh) opt out of the refresh-on-401 path so we
// never recursively try to refresh a refresh request — that previously could
// park the refresh promise forever via failedQueue.
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    if (!originalRequest) {
      return Promise.reject(new ApiError('No original request configuration.', undefined, undefined));
    }

    if (error.response) {
      const status = error.response.status;

      if (status === 401 && isDevAuthBypassEnabled()) {
        return Promise.reject(
          new ApiError(error.message, status, error.response?.data)
        );
      }

      const isAuthBypassed = Boolean((originalRequest as { skipAuth?: boolean }).skipAuth);

      if (status === 401 && !isAuthBypassed && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise<string | null>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              // Same guard as the request that started the refresh: a 401 on this replay
              // must not start another refresh.
              originalRequest._retry = true;
              if (originalRequest.headers && token) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return axiosInstance(originalRequest);
            })
            .catch(err => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = getStoredRefreshToken();
        if (!refreshToken) {
          clearTokens();
          if (onSessionExpired) {
            onSessionExpired();
          }
          isRefreshing = false;
          return Promise.reject(new AuthError('No refresh token available.'));
        }

        try {
          // _retry: true on the refresh call itself prevents recursive refresh
          // if /api/auth/refresh ever returns 401.
          const response = await axiosInstance.post(
            '/api/auth/refresh',
            { refresh_token: refreshToken },
            { skipAuth: true, _retry: true } as any,
          );
          const newAccessToken = response.data.access_token;
          const newRefreshToken = response.data.refresh_token || refreshToken;

          storeTokens(newAccessToken, newRefreshToken);
          axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;

          processQueue(null, newAccessToken);
          return axiosInstance(originalRequest);
        } catch (refreshError: any) {
          // Another flow (a password change, which revokes every older session and hands this
          // tab a fresh pair, or another tab's refresh) stored new tokens while this refresh was
          // in flight with the old one. That isn't an expired session: replay with the new token
          // instead of wiping it.
          if (tokensRotatedSince(refreshToken)) {
            processQueue(null, getStoredToken());
            // _retry was set above, before the refresh. A 401 on this replay falls through
            // to a normal error instead of starting another refresh.
            return axiosInstance(originalRequest);
          }
          processQueue(refreshError, null);
          clearTokens();
          if (onSessionExpired) {
            onSessionExpired();
          }
          return Promise.reject(
            new AuthError('Token refresh failed.', refreshError.response?.status, refreshError.response?.data)
          );
        } finally {
          isRefreshing = false;
        }
      }

      if (status === 404) {
        return Promise.reject(new NotFoundError('The requested resource was not found.', error.response.data));
      }

      return Promise.reject(new ApiError(error.message, status, error.response.data));
    }

    return Promise.reject(new ApiError(error.message));
  }
);

export default axiosInstance;
